import React, {
  forwardRef,
  MutableRefObject,
  RefObject,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Button } from '@blueprintjs/core';
import { FormTemplate, FormTemplateSection, FormTemplateValidator, } from 'data/template/template.types';
import * as types from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import MultiTab from 'shared/multi-tab';
import { TabProp } from 'shared/multi-tab/MultiTab';
import Form, {
  CustomFormFieldComponent,
  FieldUpdateHandlerParam,
} from 'shared/form';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from 'i18n/i18n.types';
import { TargetTemplateFormSection } from 'routes/TargetDeliveryTemplateAccessFunction/TargetDeliveryTemplateAccessFunction.types';
import { getInterfaceFormTemplateSection, adaptInterfaceTemplateState } from '../../utils/targetDeliveryTemplate.adapter';
import hideInterfaceSection from '../../utils/hideInterfaceSection';
import showInterfaceProtocolForm from '../../utils/showInterfaceProtocolForm';
import './target-delivery-template-interface-form.scss';

export type FieldHandler = (param: FieldUpdateHandlerParam, formSection?: 'main' | 'targetInformation' | 'callContentChannels', tabId?: string) => void

type Props = {
  id: string
  title: string
  subTitle?: string
  tabTitle: string
  formSection: TargetTemplateFormSection
  isEditing: boolean
  template: FormTemplate
  data: Partial<types.TargetDeliveryTemplateAccessFunctionFormData>
  displayMultiTab?: boolean
  customFieldComponentMap?: { [key: string]: CustomFormFieldComponent }
  i18nKeyPrefix: string
  onFieldUpdate?: (param: FieldUpdateHandlerParam, formSection: TargetTemplateFormSection, tabId?: string) => void
  onFieldCreateNew?: (param: FieldUpdateHandlerParam, formSection: TargetTemplateFormSection, tabId?: string) => void
  onInterfaceAdd?: (formSection: TargetTemplateFormSection) => void
  onInterfaceRemove?: (id: string | number, formSection: TargetTemplateFormSection) => void
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.TargetDeliveryTemplateInterfaceFormData[]
  getMainFormValues: () => types.TargetDeliveryTemplateInterfaceFormData[]
}

export const TargetDeliveryTemplateInterfaceForm = forwardRef<Ref, Props>(({
  id,
  title,
  subTitle,
  tabTitle,
  formSection,
  isEditing,
  template,
  data,
  customFieldComponentMap,
  i18nKeyPrefix,
  displayMultiTab = false,
  onFieldUpdate = () => null,
  onFieldCreateNew = () => null,
  onInterfaceAdd = () => null,
  onInterfaceRemove = () => null,
}: Props, ref) => {
  const { t } = useTranslation();
  const interfaceRefs = useRef<(RefObject<Form>)[]>([]);
  const [invalidInterfacesTabs, setInvalidInterfacesTabs] = useState<{ [id: string]: boolean }>({});
  const mainFormRef = useRef<Form>(null);

  const formTemplateSection = getInterfaceFormTemplateSection(formSection, data, template);

  const getValidTabs = (
    tabFormRefs: MutableRefObject<RefObject<Form>[]>,
    forceValidation = false,
    forceTouched = true,
  ) => {
    const validFormTabs = tabFormRefs.current
      .filter((formRef) => !!formRef.current)
      .map((formRef) => formRef.current?.isValid(forceValidation, forceTouched) ?? true);

    return validFormTabs;
  };

  const isValid = (forceValidation = false, forceTouched = true) => {
    const validInterfaces = getValidTabs(interfaceRefs, forceValidation, forceTouched);
    const allInterfacesValid = validInterfaces.every((valid) => valid === true);

    const invalidInterfaces = validInterfaces.reduce((previousValue, entry, i) => {
      const interfaceData = data[formSection];

      if (interfaceData == null) {
        return previousValue;
      }

      const key = interfaceData?.[i]?.id;
      if (key == null) {
        return previousValue;
      }

      return {
        ...previousValue,
        [key]: !entry,
      };
    }, {});

    setInvalidInterfacesTabs(invalidInterfaces);

    const mainFormValid = mainFormRef.current?.isValid(forceValidation, forceTouched) ?? true;

    return allInterfacesValid && mainFormValid;
  };

  const getValues = () => {
    const formValues: types.TargetDeliveryTemplateInterfaceFormData[] = [
      ...interfaceRefs.current
        .filter((interfaceRef) => !!interfaceRef.current)
        .map((interfaceRef) => interfaceRef.current?.getValues()),
    ];

    return formValues;
  };

  const getMainFormValues = () => {
    return mainFormRef.current?.getValues();
  };

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
    getMainFormValues,
  }));

  const shortIntValidator = (
    _propFields?: unknown,
    _stateFields?: unknown,
    value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
  ) => {
    const values = value ?? '0';
    const intValue = parseInt(values, 10);
    const valid = (intValue >= 0 && intValue <= 65535);
    let message = '';
    if (!valid && validator?.customValidationFunction) {
      message = t(`${i18nKeyPrefix}.fieldError.${validator.customValidationFunction}` as TranslationKey) ?? '';
    }

    return { isValid: valid, errorMessage: message };
  };

  const customValidationFunctionMap = {
    shortIntValidator
  };

  const createInterfaceTab = (
    tabData: Partial<types.TargetDeliveryTemplateInterfaceFormData> = {},
    index: number,
  ): TabProp | undefined => {
    if (!formTemplateSection) {
      return;
    }
    if (tabData.id == null) {
      return;
    }

    const interfaceRef: { current: Form | null } = {
      current: null,
    };

    interfaceRefs.current.push(interfaceRef);

    const refCallback = (node: Form | null) => {
      interfaceRef.current = node;
    };

    const handleFieldUpdate = (param: FieldUpdateHandlerParam) => onFieldUpdate(param, formSection, tabData.id);

    const panel = (
      <Form
        ref={refCallback}
        name={template.formId ?? template.key}
        defaults={tabData}
        fields={adaptInterfaceTemplateState((formTemplateSection as FormTemplateSection).fields, tabData, isEditing)}
        layout={(formTemplateSection as FormTemplateSection).metadata.layout?.rows}
        collapsibleLayout={(formTemplateSection as FormTemplateSection).metadata.layout?.collapsible}
        i18nKeyPrefix={i18nKeyPrefix}
        fieldUpdateHandler={handleFieldUpdate}
        customFieldComponentMap={customFieldComponentMap}
        customValidationFunctionMap={customValidationFunctionMap}
      />
    );

    return {
      id: tabData.id,
      title: `${tabTitle} ${index + 1}`,
      panel,
      hasErrors: !!invalidInterfacesTabs?.[tabData.id],
    };
  };

  const onTabAdd = () => onInterfaceAdd(formSection);
  const onTabRemove = (tabId: string | number) => onInterfaceRemove(tabId, formSection);
  const onTabFieldUpdate = (param: FieldUpdateHandlerParam) => onFieldUpdate(param, formSection);
  const onTabFieldCreateNew = (param: FieldUpdateHandlerParam) => onFieldCreateNew(param, formSection);

  const tabs = data[formSection]?.map(createInterfaceTab).filter((entry): entry is TabProp => entry != null) ?? [];

  if (hideInterfaceSection(formTemplateSection, isEditing, tabs, data)) {
    return null;
  }

  if (tabs.length === 0) {
    return (
      <div data-testid={`TargetDeliveryTemplateInterfaceForm${id}`}>
        <h3>{title}</h3>
        {subTitle && <p className="instructional-text">{subTitle}</p>}
        <Button data-testid="TargetDeliveryTemplateInterfaceFormAdd" icon="plus" onClick={onTabAdd} className="add-button" />
      </div>
    );
  }

  if (!displayMultiTab) {
    if (tabs.length === 1) {
      return (
        <div data-testid={`TargetDeliveryTemplateInterfaceForm${id}`}>
          <h3>{title}</h3>
          {subTitle && <p className="instructional-text">{subTitle}</p>}
          <div className="multi-tab-form-section" data-testid={`MultiTabFormSection${id}`}>
            { tabs[0].panel }
          </div>
        </div>
      );
    }
  }

  return (
    <div data-testid={`TargetDeliveryTemplateInterfaceForm${id}`}>
      <h3>{title}</h3>
      {subTitle && <p className="instructional-text">{subTitle}</p>}
      <div className="multi-tab-form-section" data-testid={`MultiTabFormSection${id}`}>
        <MultiTab
          tabContainerId={id}
          tabs={tabs}
          addTab={onTabAdd}
          removeTab={onTabRemove}
          minNumberOfTabs={formTemplateSection?.metadata.min}
          maxNumberOfTabs={formTemplateSection?.metadata.max}
          isEditing={isEditing}
        />
      </div>
    </div>
  );
});

export default TargetDeliveryTemplateInterfaceForm;
